import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { TextWithTooltip } from '../../TextWithTooltip';
import { font, ChartCell, ChartCellWrapper } from './sharedStyles';
import { getColumnBorderFlags } from './columnBorderFlags';

const LinkCellWrapper = styled.div`
  position: absolute;
  top: 5px;
  left: 0;
  right: 0;
  ${font}
  letter-spacing: -0.3px;
  padding-right: 12px !important;
  max-width: 100%;
`;

const Link = styled.a`
  color: inherit;
  text-decoration: underline;
  &:hover {
    text-decoration: underline;
  }
  display: inline-block;
  max-width: 100%;
  cursor: pointer;
`;

const HiddenMeasure = styled.span`
  position: absolute;
  visibility: hidden;
  pointer-events: none;
  white-space: nowrap;
  font: inherit;
  letter-spacing: inherit;
`;

const LinkText = styled.span`
  display: inline;
`;

const normalizeLink = (value: unknown) => {
  if (!value || typeof value !== 'object') {
    return { text: value, url: undefined };
  }

  const possible = value as { text?: unknown; url?: unknown };
  const text = possible.text ?? value;
  const url = typeof possible.url === 'string' ? possible.url : undefined;

  return { text, url };
};

type LinkContentProps = {
  text: string;
  url?: string;
  isFirst: boolean;
  isLast: boolean;
};

const LinkContent: React.FC<LinkContentProps> = ({ text, url, isFirst, isLast }) => {
  const linkContainerRef = useRef<HTMLDivElement | null>(null);
  const measureRef = useRef<HTMLSpanElement | null>(null);
  const [displayText, setDisplayText] = useState(text);
  const [isTruncated, setIsTruncated] = useState(false);

  const updateDisplayText = useCallback(() => {
    const measure = measureRef.current;
    if (!measure) return;

    const container = linkContainerRef.current;
    const availableWidth = container?.getBoundingClientRect().width ?? container?.clientWidth ?? 0;
    const fallbackWidth = container?.parentElement?.getBoundingClientRect().width ?? 0;
    const resolvedWidth = availableWidth > 0 ? availableWidth : fallbackWidth;

    if (!container || resolvedWidth <= 0) {
      measure.textContent = text;
      setDisplayText(text);
      setIsTruncated(false);
      return;
    }

    const fullText = text;
    if (!fullText) {
      setDisplayText('');
      setIsTruncated(false);
      return;
    }

    if (resolvedWidth <= 0 || fullText.length <= 3) {
      measure.textContent = fullText;
      setDisplayText(fullText);
      setIsTruncated(false);
      return;
    }

    measure.textContent = fullText;
    const fullWidth = measure.offsetWidth;
    if (fullWidth <= resolvedWidth) {
      setDisplayText(fullText);
      setIsTruncated(false);
      return;
    }

    let low = 0;
    let high = Math.max(0, fullText.length - 3);
    let bestCandidate = '...';

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      const candidate = `${fullText.slice(0, mid)}...`;
      measure.textContent = candidate;
      if (measure.offsetWidth <= resolvedWidth) {
        bestCandidate = candidate;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    setDisplayText(bestCandidate);
    setIsTruncated(true);
    measure.textContent = fullText;
  }, [text]);

  useLayoutEffect(() => {
    updateDisplayText();
  }, [updateDisplayText]);

  useEffect(() => {
    const container = linkContainerRef.current;
    if (!container) return;

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => {
        updateDisplayText();
      });
      observer.observe(container);
      return () => observer.disconnect();
    }

    const handleResize = () => updateDisplayText();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [updateDisplayText]);

  useEffect(() => {
    setDisplayText(text);
    setIsTruncated(false);
  }, [text]);

  const tooltipContent = <span style={{ textDecoration: 'none' }}>{text}</span>;
  const contents = url ? (
    <TextWithTooltip forceTooltip={isTruncated} tooltipContent={tooltipContent}>
      <Link href={url} target="_blank" rel="noopener noreferrer">
        <LinkText>{displayText}</LinkText>
      </Link>
    </TextWithTooltip>
  ) : (
    <TextWithTooltip forceTooltip={isTruncated} tooltipContent={tooltipContent}>
      <LinkText>{displayText}</LinkText>
    </TextWithTooltip>
  );

  return (
    <ChartCell $isfirst={isFirst ? 1 : 0} $islast={isLast ? 1 : 0}>
      <ChartCellWrapper>
        <LinkCellWrapper ref={linkContainerRef}>
          {contents}
          <HiddenMeasure ref={measureRef} aria-hidden="true">
            {text}
          </HiddenMeasure>
        </LinkCellWrapper>
      </ChartCellWrapper>
    </ChartCell>
  );
};

export const LinkRenderer =
  (firstColumnIds: Set<string>, lastColumnIds: Set<string>, columnIds: string[], hasScroll: boolean | undefined) =>
  // eslint-disable-next-line react/display-name
  ({
    cellData,
    rowData,
    dataKey,
    columnIndex,
  }: {
    cellData: unknown;
    rowData: any;
    dataKey: string;
    columnIndex?: number;
  }) => {
    const { isFirst, isLast } = getColumnBorderFlags(columnIds, columnIndex, firstColumnIds, lastColumnIds, hasScroll);
    const rawValue = rowData?.[dataKey];
    const urlFromHiddenField = rowData?.[`${dataKey}__url`];
    const fallbackValue = rawValue ?? cellData;
    const normalizedSource =
      typeof urlFromHiddenField === 'string' && urlFromHiddenField.trim().length > 0
        ? { text: fallbackValue, url: urlFromHiddenField }
        : fallbackValue;
    const { text, url } = normalizeLink(normalizedSource);
    const safeText = text === null || text === undefined ? '' : String(text);

    return (
      <LinkContent text={safeText} url={typeof url === 'string' ? url : undefined} isFirst={isFirst} isLast={isLast} />
    );
  };
